import { ThumbsDownIcon, ThumbsUpIcon } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/utils';
import type {
  PersonaVote,
  PersonaVoteRecord,
  ScoutTargetingPersona,
} from '@/modules/scout-sourcing/types';
import { Avatar, AvatarFallback } from '@/modules/ui/components/avatar/Avatar';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';

type Props = {
  profiles: ScoutTargetingPersona[];
  onSubmitVotes: (votes: PersonaVoteRecord[]) => void;
  disabled?: boolean;
  loading?: boolean;
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

export function TargetingRound({
  profiles,
  onSubmitVotes,
  disabled,
  loading,
}: Props) {
  const [votes, setVotes] = useState<Record<string, PersonaVote>>({});

  const setVote = (id: string, vote: PersonaVote) =>
    setVotes((prev) => ({ ...prev, [id]: vote }));

  const decidedCount = profiles.filter((p) => votes[p.id]).length;
  const allDecided = profiles.length > 0 && decidedCount === profiles.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Which of these calibration profiles are close to your target?
        </CardTitle>
        <CardDescription>
          These are calibration profiles, not real people. Choose the ones that
          best represent your target candidate and Scout will use them to refine
          the search before sourcing real candidates.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {profiles.length === 0 ? (
          <p className="text-neutral-650 px-6 py-8 text-center text-sm">
            Building the next set of profiles…
          </p>
        ) : (
          <ul className="divide-border divide-y">
            {profiles.map((p) => (
              <li
                key={p.id}
                className="flex items-start gap-4 px-6 py-4 first:pt-2"
              >
                <Avatar className="bg-secondary-150 size-9">
                  <AvatarFallback className="bg-secondary-150 text-primary-700 text-xs font-semibold">
                    {initials(p.name)}
                  </AvatarFallback>
                </Avatar>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-foreground font-medium">
                      {p.name}
                    </span>
                    <Badge variant="outline">{p.archetype}</Badge>
                  </div>
                  <p className="text-neutral-650 text-sm">
                    {p.currentRole} @ {p.currentCompany} · {p.location}
                  </p>
                  {p.archetypeLabel && (
                    <p className="text-foreground/80 mt-1.5 text-sm">
                      {p.archetypeLabel}
                    </p>
                  )}
                  {p.skills.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {p.skills.map((s) => (
                        <Badge key={s} variant="secondary">
                          {s}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {p.salaryMin != null && p.salaryMax != null && (
                    <p className="text-neutral-650 mt-1.5 text-xs">
                      Target salary: {p.salaryCurrency ?? ''} {p.salaryMin}–
                      {p.salaryMax}
                    </p>
                  )}
                </div>

                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  <div className="flex gap-2">
                    <VoteToggle
                      label="Would source"
                      active={votes[p.id] === 'up'}
                      activeClassName="border-secondary-500 bg-secondary-500 text-white"
                      disabled={disabled}
                      onClick={() => setVote(p.id, 'up')}
                    >
                      <ThumbsUpIcon className="size-4" />
                    </VoteToggle>
                    <VoteToggle
                      label="Pass"
                      active={votes[p.id] === 'down'}
                      activeClassName="border-destructive bg-destructive text-white"
                      disabled={disabled}
                      onClick={() => setVote(p.id, 'down')}
                    >
                      <ThumbsDownIcon className="size-4" />
                    </VoteToggle>
                  </div>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setVote(p.id, 'skip')}
                    className={cn(
                      'rounded-md px-1.5 py-0.5 text-xs transition-colors disabled:pointer-events-none disabled:opacity-50',
                      votes[p.id] === 'skip'
                        ? 'text-foreground font-medium'
                        : 'text-neutral-650 hover:text-foreground',
                    )}
                  >
                    Not sure
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {profiles.length > 0 && (
          <div className="border-border flex items-center justify-between border-t px-6 py-4">
            <span className="text-neutral-650 text-sm">
              {decidedCount} of {profiles.length} reviewed
            </span>
            <Button
              disabled={disabled || !allDecided}
              loading={loading}
              onClick={() => {
                const records: PersonaVoteRecord[] = profiles.map((p) => ({
                  profileId: p.id,
                  vote: votes[p.id] ?? 'skip',
                  archetype: p.archetype,
                }));
                onSubmitVotes(records);
              }}
            >
              Continue
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface VoteToggleProps {
  label: string;
  active?: boolean;
  activeClassName: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function VoteToggle({
  label,
  active,
  activeClassName,
  disabled,
  onClick,
  children,
}: VoteToggleProps) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex size-9 items-center justify-center rounded-full border transition-colors disabled:pointer-events-none disabled:opacity-50',
        active
          ? activeClassName
          : 'border-border text-neutral-650 hover:text-foreground hover:border-neutral-400',
      )}
    >
      {children}
    </button>
  );
}
